import React, { useState } from 'react';
import axios from 'axios';

// A fuller client: blurb / profile / raw-MCP modes, logging in from SAIL_ANALYTICS_USER /
// SAIL_ANALYTICS_PASSWORD. The repo is private (cmu-ace-ai org members only), and the
// script is not on main yet: switch the branch here once backbone-lo-characterization merges.
const DEMO_SCRIPT_URL =
  'https://github.com/cmu-ace-ai/aceai-data-backbone/blob/backbone-lo-characterization/tools/learner_state_demo.py';

// The REST endpoints a script can call with a Bearer token (backend/main.py).
const ENDPOINTS = [
  { method: 'GET', path: '/me', what: 'Who the token belongs to: username, role, real-name courses.' },
  { method: 'GET', path: '/courses', what: 'Course catalog, and which courses this user may see with real names.' },
  {
    method: 'GET',
    path: '/students/{course_slug}?data_mode=anon|real',
    what: 'Students in a course (pseudonyms in anon mode).',
  },
  {
    method: 'GET',
    path: '/student-snapshot?course_slug=…&student_id=…&data_mode=anon|real',
    what:
      "One student's learner-model profile: metrics, per-topic mastery (metrics_by_topic), " +
      'prose summaries (metric_summaries) and recent evidence.',
  },
  {
    method: 'POST',
    path: '/query-agent',
    what:
      'Ask the chat assistant a question. JSON body: course_slug, question, acting_as ' +
      '("instructor" or a student id), data_mode, optional history.',
  },
  {
    method: 'POST',
    path: '/token',
    what:
      'Get a token with a username and password instead of this panel. JSON body: username, ' +
      'password, optional course_slug, data_mode, acting_as.',
  },
  {
    method: 'GET / POST',
    path: '/mcp/tools, /mcp/tools/{name}/call',
    what: 'List and call MCP tools directly (admin accounts only).',
  },
];

function pythonSample({ apiBaseUrl, token, courseSlug, dataMode, studentId, username }) {
  return `import os
import requests

BASE = "${apiBaseUrl}"


def get_token():
    """A fresh one-hour token. Set SAIL_ANALYTICS_USER and SAIL_ANALYTICS_PASSWORD in the
    environment (never in this file); a long-running script can call this again when a
    request returns 401. Without them, falls back to the token pasted below."""
    user = os.environ.get("SAIL_ANALYTICS_USER")
    password = os.environ.get("SAIL_ANALYTICS_PASSWORD")
    if user and password:
        r = requests.post(f"{BASE}/token", json={"username": user, "password": password})
        r.raise_for_status()
        return r.json()["access_token"]
    return "${token || '<press Get token in the API panel, or set the variables above>'}"


headers = {"Authorization": f"Bearer {get_token()}"}
r = requests.get(
    f"{BASE}/student-snapshot",
    headers=headers,
    params={
        "course_slug": "${courseSlug}",
        "student_id": "${studentId || '<a student id from /students/...>'}",
        "data_mode": "${dataMode}",
    },
)
r.raise_for_status()
snapshot = r.json()
for key, summary in snapshot["metric_summaries"].items():
    print(f"{key}: {summary}")

# Run with your own login:
#   export SAIL_ANALYTICS_USER=${username || '<your username>'}
#   export SAIL_ANALYTICS_PASSWORD=...   # ask the site admin if you don't know it
`;
}

function ApiPanel({ apiBaseUrl, courseSlug, dataMode, studentId, username, onClose }) {
  const [token, setToken] = useState('');
  const [expiresIn, setExpiresIn] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const code = pythonSample({ apiBaseUrl, token, courseSlug, dataMode, studentId, username });

  const getToken = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${apiBaseUrl}/api-token`);
      setToken(res.data.access_token);
      setExpiresIn(res.data.expires_in);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      setError('Could not copy; select the code and copy it by hand.');
    }
  };

  return (
    <div className="mcp-panel">
      <div className="mcp-panel-header">
        <span>API</span>
        <div className="mcp-panel-header-actions">
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>

      <div className="mcp-panel-body">
        <section className="mcp-section">
          <h4>Endpoints</h4>
          <p className="mcp-muted">
            Base URL <code>{apiBaseUrl}</code>. Send <code>Authorization: Bearer &lt;token&gt;</code>.
          </p>
          <ul className="api-endpoints">
            {ENDPOINTS.map((e) => (
              <li key={e.path}>
                <code>{e.method} {e.path}</code>
                <div className="mcp-muted">{e.what}</div>
              </li>
            ))}
          </ul>
        </section>

        <section className="mcp-section">
          <h4>Your token</h4>
          <p className="mcp-muted">
            A token for your account, with exactly your access. Treat it like a password: anyone
            holding it can read what you can, until it expires.
          </p>
          <button type="button" className="api-button" onClick={getToken} disabled={loading}>
            {loading ? 'Getting token…' : token ? 'Get a new token' : 'Get token'}
          </button>
          {expiresIn && (
            <span className="mcp-muted"> expires in {Math.round(expiresIn / 60)} minutes</span>
          )}
          {error && <p className="mcp-error">{error}</p>}
        </section>

        <section className="mcp-section">
          <h4>Python</h4>
          <p className="mcp-muted">
            A ready-made client that prints a learner&apos;s state three ways:{' '}
            <a href={DEMO_SCRIPT_URL} target="_blank" rel="noreferrer">
              tools/learner_state_demo.py
            </a>{' '}
            (GitHub; needs access to the cmu-ace-ai organization).
          </p>
          <details>
            <summary className="mcp-muted">Or a minimal example to copy</summary>
            <p className="mcp-muted">
              Fetches a student snapshot for the current course and data mode. It logs in with
              <code>SAIL_ANALYTICS_USER</code> / <code>SAIL_ANALYTICS_PASSWORD</code> from the
              environment and gets its own token; without them it uses the one-hour token
              {token ? ' filled in below' : ' from "Get token"'}. Needs <code>pip install requests</code>.
            </p>
            <pre className="api-code">{code}</pre>
            <button type="button" className="api-button" onClick={copy}>
              {copied ? 'Copied' : 'Copy code'}
            </button>
          </details>
        </section>
      </div>
    </div>
  );
}

export default ApiPanel;
